<template>
  <div class="summaryContainer d-flex flex-column pb-4">
    <div class="summaryHeaderContainer d-flex justify-space-between align-center px-4">
      <div class="d-flex align-center">
        <span class="summaryTitle mr-2">
          {{ $t('extension.summary.participants') }}
        </span>
        <div class="d-flex flex-row">
          <AnalysisAvatars
            v-if="analysis && analysis.id"
            :analysis="analysis"
            :user-analyses="analysis.userAnalyses"
            :show-badge="false"
            :show-number-of-extra-avatars="true"
            :max-avatars="5"
            overlap="-12px"
          />
        </div>
      </div>
      <AnalysisSelector
        :document-details="documentDetails"
        :current-analysis-id="analysis.id ? analysis.id : analysisId"
        :on-prepared-analysis="onPreparedAnalysis"
        @selectAnalysis="selectAnalysis"
      />
    </div>
    <div class="tagsContainer my-4 d-flex">
      <span class="labels align-self-center">
        {{ $t('extension.summary.tags') }}
      </span>
      <TagsAutocomplete
        style="width: auto;"
        :tags="tags"
        :default-selected-tags="defaultSelectedTags"
        @selectedTags="selectTags"
      />
    </div>

    <div v-if="showSummary">
      <div class="labels d-flex">
        <span>
          {{ $t('extension.summary.evaluationTitle') }}
        </span>
        <Rating
          class="pl-8"
          :read-only="true"
          :score="averageScore"
          color="#3C44C8"
        />
        <span class="evaluationDetails">
          {{ ratingFromScore(averageScore) }} ({{ evaluationString }})
        </span>
      </div>
      <div class="labels d-flex mt-5">
        <span>
          {{ $t('extension.summary.topElementsTitle') }}
        </span>
      </div>
      <TopElement
        v-for="element in topElements"
        :key="element.id"
        :element="element"
      />
    </div>
    <div class="emptySummaryText d-flex mx-4" v-else>
      {{ $t('extension.summary.emptySummaryText') }}
    </div>
  </div>
</template>
<script>
import { mapGetters, mapActions } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import AnalysisSelector from './AnalysisSelector';
import AnalysisAvatars from './AnalysisAvatars';
import TagsAutocomplete from '../Tags/TagsAutocomplete';
import Rating from '../ExpressAnalysis/EvaluationForm/Rating';
import TopElement from './TopElement';
import { types as userTypes } from '../../store/user';
import { types as analysesTypes } from '../../store/analyses';
import { types as portfolioTypes } from '../../store/portfolio';
import { types as tagsTypes } from '../../store/tags';
import { ratingFromScore } from '../ExpressAnalysis/EvaluationForm/ratingHelper';

export default {
  name: 'AnalysisSummary',
  components: {
    AnalysisSelector,
    AnalysisAvatars,
    TagsAutocomplete,
    Rating,
    TopElement,
  },
  props: {
    analysis: {
      type: Object,
      default() { return {}; },
    },
    analysisId: {
      type: String,
      default: '',
    },
    documentDetails: {
      type: Object,
      required: true,
    },
    onPreparedAnalysis: {
      type: Boolean,
      default: false,
    },
  },
  created() {
    // When coming from the FicheInfo, this.userId is already defined, we fetch tags here.
    // When coming from the extension, this.userId will arrive a little later, see watcher below
    if (this.userId !== undefined) {
      this.fetchOrganizationTags();
      this.fetchUserTags();
    }
  },
  data() {
    return {
      participants: 0,
      tags: [],
      defaultSelectedTags: [],
      isInPersonalPortfolio: undefined,
    };
  },
  computed: {
    ...mapGetters({
      userTags: tagsTypes.getters.userTags,
      organizationTags: tagsTypes.getters.organizationTags,
      analysisInPersonalPortfolio: portfolioTypes.getters.analysisInPersonalPortfolio,
      userId: userTypes.getters.userId,
    }),
    topElements() {
      return get(this.analysis, 'summary.topElements', []);
    },
    averageScore() {
      return get(this.analysis, 'summary.evaluationAverage', -1);
    },
    nbEvaluations() {
      return get(this.analysis, 'summary.evaluationCount', -1);
    },
    showSummary() {
      return this.nbEvaluations > 0 || this.topElements.length > 0;
    },
    evaluationString() {
      return this.$tc('extension.summary.evaluation', this.nbEvaluations, { count: this.nbEvaluations });
    },
  },
  methods: {
    ratingFromScore,
    ...mapActions({
      addUserTags: tagsTypes.actions.addUserTags,
      fetchOrganizationTags: tagsTypes.actions.fetchOrganizationTags,
      fetchUserTags: tagsTypes.actions.fetchUserTags,
      updateAnalysisTags: analysesTypes.actions.updateAnalysisTags,
    }),
    selectAnalysis(analysis) {
      this.$emit('selectAnalysis', analysis);
    },
    async selectTags({ existingTags, newTags }) {
      const tags = [];
      const savedNewTags = await this.addUserTags({ tags: newTags });

      existingTags.forEach(tag => tags.push(tag));
      savedNewTags.forEach(tag => tags.push(tag));

      this.updateAnalysisTags(tags);
    },
    getChipColor(item) {
      if (item.source === 'organization') {
        return '#FFEFAF';
      }

      return '#AFE1FF';
    },
    updateTags() {
      this.tags = [...this.organizationTags, ...this.userTags];
    },
  },
  watch: {
    async analysis() {
      if (this.analysis === undefined) return;

      this.isInPersonalPortfolio = await this.analysisInPersonalPortfolio(
        this.userId, this.analysis.id, this.analysis.documentId,
      );

      if (this.analysis.tags) {
        this.defaultSelectedTags = cloneDeep(this.analysis.tags);
      } else {
        this.defaultSelectedTags = [];
      }
    },
    organizationTags() {
      this.updateTags();
    },
    userTags() {
      this.updateTags();
    },
    async userId() {
      if (this.userId !== undefined) {
        this.fetchOrganizationTags();
        this.fetchUserTags();
      }
    },
  },
};
</script>
<style scoped>
.summaryContainer {
  border-bottom: 1px solid #ced2ee;
  background-color: white;
}
.summaryHeaderContainer {
  height: 45px;
  border-bottom: 1px solid #ced2ee;
  border-top: 1px solid #ced2ee;
  background-color: #F6F7FF;
}
.summaryTitle {
  font-size: 14px;
  font-weight: 600;
}
.labels {
  background-color: white;
  font-size: 13px;
  font-weight: 600;
  margin-left: 16px;
}
.evaluationDetails {
  color: rgba(0, 0, 0, 0.6);
  font-size: 13px;
  margin-left: 24px;
}
.participants {
  color: #3F47CD;
  font-weight: 600;
}
.tagsContainer {
  background-color:  white;
}
.tag-chip {
  font-size: 12px;
  font-weight: 525;
  height: 24px;
  border-radius: 5px;
}
::v-deep #autocomplete {
  min-width: 0px;
  width: 0px;
}
::v-deep div[aria-expanded="true"] > div > div > #autocomplete {
  width: 100%;
}
::v-deep .v-select__slot .v-input__append-inner .v-input__icon .v-icon {
  cursor: pointer;
}
::v-deep .v-select__slot .v-input__append-inner .v-input__icon .v-icon:hover {
  color: #4b53cc;
}
.emptySummaryText {
  font-size: 14px;
  margin: auto;
  color: #c8c8c8;
  padding: 0 15px;
}
</style>
